import React from "react";
import {
  ArrowLeft,
  Eye,
  Heart,
  MessageCircle,
  Share2,
  Repeat2,
  Bookmark,
  Send,
  ThumbsUp,
  Globe,
  Lightbulb,
  MoreHorizontal,
  ExternalLink,
  BarChart3,
  MousePointerClick,
  Users,
  UserPlus,
  TrendingUp,
  Play,
  Target,
  Activity,
  Info,
} from "lucide-react";
import { ContentLog } from "../../../types/dashboard";
import { BrandDetails } from "../../../types/add-brand-types";
import {
  PlatformAnalytics,
  MetricDef,
  formatNumber,
  normalizePlatformId,
  getPlatformMeta,
  getPlatformMetricDefs,
} from "../../../types/post-analytics";
import usePostAnalytics from "../../../hooks/use-post-analytics";
import { MediaCarousel } from "./creator-studio/media-carousel";
import xIcon from "../../../assets/images/social/x-twitter.svg";
import redditIcon from "../../../assets/images/social/reddit.svg";
import threadsIcon from "../../../assets/images/social/threads.svg";
import "./post-analytics.css";
import "../tabs/creator-studio/step3.css";

interface PostAnalyticsScreenProps {
  item: ContentLog;
  brandDetails: BrandDetails;
  onBack: () => void;
}

/* ─── Icon resolver (types file stores iconName as string) ─── */

const ICON_MAP: Record<string, React.ReactNode> = {
  Eye: <Eye size={18} />,
  Heart: <Heart size={18} />,
  MessageCircle: <MessageCircle size={18} />,
  Share2: <Share2 size={18} />,
  Repeat2: <Repeat2 size={18} />,
  Bookmark: <Bookmark size={18} />,
  MousePointerClick: <MousePointerClick size={18} />,
  ExternalLink: <ExternalLink size={18} />,
  ThumbsUp: <ThumbsUp size={18} />,
  Users: <Users size={18} />,
  UserPlus: <UserPlus size={18} />,
  TrendingUp: <TrendingUp size={18} />,
  Play: <Play size={18} />,
  Target: <Target size={18} />,
  Activity: <Activity size={18} />,
};

const resolveIcon = (name: string): React.ReactNode =>
  ICON_MAP[name] ?? <Eye size={18} />;

/* ─── Shared sub-components ─── */

const renderTextWithHashtags = (text: string) => {
  const parts = text.split(/(#\w+)/g);
  return parts.map((part, i) =>
    part.startsWith("#") ? (
      <span key={i} style={{ color: "#3b82f6", fontWeight: 500 }}>
        {part}
      </span>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
};

const Avatar: React.FC<{
  src?: string;
  alt?: string;
  size: number;
  borderRadius?: string;
  className?: string;
}> = ({ src, alt = "", size, borderRadius = "50%", className }) =>
  src ? (
    <img
      src={src}
      alt={alt}
      className={className}
      style={{ width: size, height: size, borderRadius, objectFit: "cover" }}
    />
  ) : (
    <div
      className={className}
      style={{ width: size, height: size, background: "#1f2937", borderRadius }}
    />
  );

/* ─── Platform analytics card ─── */

const PlatformAnalyticsCard: React.FC<{ platform: PlatformAnalytics }> = ({
  platform,
}) => {
  const meta = getPlatformMeta(platform.platform);
  const metricDefs = getPlatformMetricDefs(platform);
  const isLinkedIn = normalizePlatformId(platform.platform) === "linkedin";

  return (
    <div className="pa-platform-card">
      <div className="pa-platform-card-header">
        <div className="pa-platform-card-info">
          <div className="pa-platform-card-icon">
            {meta.icon && <img src={meta.icon} alt={meta.name} />}
          </div>
          <div>
            <div className="pa-platform-card-name">{meta.name}</div>
            {platform.posted_at && (
              <div className="pa-posted-at">
                Posted {new Date(platform.posted_at).toLocaleString()}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <span
            className={`pa-platform-card-status ${platform.success ? "success" : "failed"}`}
          >
            {platform.success ? "Published" : "Failed"}
          </span>
          {platform.platform_url && (
            <a
              href={platform.platform_url}
              target="_blank"
              rel="noopener noreferrer"
              className="pa-view-post-link"
            >
              View post <ExternalLink size={13} />
            </a>
          )}
        </div>
      </div>

      <div className="pa-metric-grid">
        {metricDefs.map((m: MetricDef) => (
          <div className="pa-metric-item" key={m.label}>
            <span className="pa-metric-item-icon">
              {resolveIcon(m.iconName)}
            </span>
            <div className="pa-metric-item-value">{formatNumber(m.value)}</div>
            <div className="pa-metric-item-label">{m.label}</div>
          </div>
        ))}
      </div>

      {isLinkedIn && (
        <div className="pa-platform-notice">
          <Info size={15} />
          <span>
            LinkedIn analytics are available only for Company Pages. Personal
            profile posts may publish successfully but return no metrics.
          </span>
        </div>
      )}

      {!platform.success && platform.failure_reason && (
        <div
          style={{
            marginTop: "14px",
            padding: "12px 14px",
            borderRadius: "12px",
            background: "#fef2f2",
            border: "1px solid #fecaca",
            color: "#991b1b",
            fontSize: "13px",
            lineHeight: 1.5,
          }}
        >
          <strong style={{ display: "block", marginBottom: "4px" }}>
            Failure reason
          </strong>
          <span>{platform.failure_reason}</span>
        </div>
      )}
    </div>
  );
};

/* ─── Main screen ─── */

const PostAnalyticsScreen: React.FC<PostAnalyticsScreenProps> = ({
  item,
  brandDetails,
  onBack,
}) => {
  const {
    data,
    loading,
    activePlatform,
    setActivePlatform,
    activePlatformData,
    mediaPreviewUrls,
    mediaMetadata,
    getPreviewAccount,
    metrics,
    postBody,
    postTitle,
    subreddits,
    twitterBookmarks,
    instagramSaved,
    facebookReactionsTotal,
    linkedInLikeCount,
    linkedInCommentCount,
    linkedInShareCount,
  } = usePostAnalytics(item, brandDetails);

  const renderMediaGrid = (borderRadius: string = "0px") => (
    <MediaCarousel
      mediaPreviewUrls={mediaPreviewUrls}
      mediaMetadata={mediaMetadata}
      borderRadius={borderRadius}
    />
  );

  /* ─── Preview renderers ─── */

  const renderTwitterPreview = () => {
    const account = getPreviewAccount("twitter") ?? getPreviewAccount("x");
    const displayName = account?.displayName ?? "Nexus AI";
    const username = account?.username ?? "nexus_ai";

    return (
      <div className="twitter-preview-container">
        <div className="twitter-header">
          <div className="preview-avatar">
            <Avatar src={account?.profilePicture} alt={displayName} size={40} />
          </div>
          <div className="twitter-user-info">
            <div className="twitter-name-row">
              <span className="twitter-name">{displayName}</span>
              <img
                src={xIcon}
                alt="X"
                style={{ width: 16, height: 16, opacity: 0.8 }}
              />
            </div>
            <span className="twitter-handle">@{username}</span>
          </div>
        </div>
        <div className="twitter-body">
          <p>{renderTextWithHashtags(postBody || "No content")}</p>
        </div>
        {mediaPreviewUrls.length > 0 && (
          <div className="twitter-media" style={{ overflow: "hidden" }}>
            {renderMediaGrid("16px")}
          </div>
        )}
        <div className="twitter-meta">
          <span>
            {activePlatformData?.posted_at
              ? new Date(activePlatformData.posted_at).toLocaleString()
              : "--"}{" "}
            ·{" "}
          </span>
          <span className="twitter-views">
            {formatNumber(metrics.views)} Views
          </span>
        </div>
        <div className="twitter-divider" />
        <div className="twitter-stats">
          <div className="stat-item">
            <Repeat2 size={14} style={{ opacity: 0.7 }} />
            <strong>{formatNumber(metrics.shares)}</strong>
          </div>
          <div className="stat-item">
            <MessageCircle size={14} style={{ opacity: 0.7 }} />
            <strong>{formatNumber(metrics.comments)}</strong>
          </div>
          <div className="stat-item">
            <Heart size={14} style={{ opacity: 0.7 }} />
            <strong>{formatNumber(metrics.likes)}</strong>
          </div>
          <div className="stat-item">
            <Bookmark size={14} style={{ opacity: 0.7 }} />
            <strong>{formatNumber(twitterBookmarks)}</strong>
          </div>
        </div>
      </div>
    );
  };

  const renderThreadsPreview = () => {
    const threadsAccount = getPreviewAccount("threads");
    const displayName = threadsAccount?.displayName ?? "Impret AI";
    const username = threadsAccount?.username ?? "impret_ai";

    return (
      <div className="threads-preview-container">
        <div className="threads-preview-header">
          <Avatar
            src={threadsAccount?.profilePicture}
            alt={displayName}
            size={44}
            className="threads-preview-avatar"
          />
          <div className="threads-preview-identity">
            <div className="threads-preview-name-row">
              <span className="threads-preview-name">{displayName}</span>
              <img
                src={threadsIcon}
                alt="Threads"
                className="threads-preview-badge"
              />
            </div>
            <div className="threads-preview-handle">@{username}</div>
          </div>
        </div>

        <div className="threads-preview-body">
          {renderTextWithHashtags(postBody || "No content")}
        </div>

        {mediaPreviewUrls.length > 0 && (
          <div className="threads-preview-media">{renderMediaGrid("18px")}</div>
        )}

        <div className="threads-preview-actions">
          <div className="threads-preview-action">
            <Heart size={16} />
            <span>{formatNumber(metrics.likes)}</span>
          </div>
          <div className="threads-preview-action">
            <MessageCircle size={16} />
            <span>{formatNumber(metrics.comments)}</span>
          </div>
          <div className="threads-preview-action">
            <Repeat2 size={16} />
            <span>
              {formatNumber(
                (activePlatformData?.raw_metrics?.reposts as number) || 0,
              )}
            </span>
          </div>
          <div className="threads-preview-action">
            <Send size={16} />
            <span>{formatNumber(metrics.shares)}</span>
          </div>
        </div>
      </div>
    );
  };

  const renderInstagramPreview = () => {
    const igAccount = getPreviewAccount("instagram");
    const igUsername = igAccount?.username ?? "nexus_ai";

    return (
      <div className="instagram-preview-container">
        <div className="ig-header">
          <div className="ig-user-row">
            <Avatar
              src={igAccount?.profilePicture}
              alt={igUsername}
              size={32}
              className="ig-avatar"
            />
            <div className="ig-username">{igUsername}</div>
          </div>
          <div className="ig-more">
            <MoreHorizontal size={20} />
          </div>
        </div>
        {mediaPreviewUrls.length > 0 && (
          <div
            className="ig-image-placeholder"
            style={{
              background: "none",
              height: "auto",
              minHeight: "300px",
              overflow: "hidden",
            }}
          >
            {renderMediaGrid()}
          </div>
        )}
        <div className="ig-actions">
          <div className="ig-action-left">
            <span className="ig-icon">
              <Heart size={22} />
            </span>
            <span className="ig-icon">
              <MessageCircle size={22} />
            </span>
            <span className="ig-icon">
              <Send size={22} />
            </span>
          </div>
          <div className="ig-action-right">
            <span className="ig-icon">
              <Bookmark size={22} />
            </span>
          </div>
        </div>
        <div className="ig-likes">{formatNumber(metrics.likes)} likes</div>
        {instagramSaved > 0 && (
          <div style={{ padding: "0 12px", fontSize: 13, color: "#9ca3af" }}>
            {formatNumber(instagramSaved)} saved
          </div>
        )}
        <div className="ig-caption">
          <strong>{igUsername}</strong>{" "}
          {renderTextWithHashtags(postBody || "No content")}
        </div>
      </div>
    );
  };

  const renderFacebookPreview = () => {
    const fbAccount = getPreviewAccount("facebook");
    const fbDisplayName = fbAccount?.displayName ?? "Nexus AI";

    return (
      <div className="facebook-preview-container">
        <div className="fb-header">
          <Avatar
            src={fbAccount?.profilePicture}
            alt={fbDisplayName}
            size={40}
            className="fb-avatar"
          />
          <div className="fb-user-info">
            <div className="fb-name">{fbDisplayName}</div>
            <div className="fb-time">
              {activePlatformData?.posted_at
                ? new Date(activePlatformData.posted_at).toLocaleDateString()
                : "Recently"}{" "}
              ·{" "}
              <Globe
                size={12}
                style={{ verticalAlign: "middle", opacity: 0.8 }}
              />
            </div>
          </div>
          <div className="fb-more">
            <MoreHorizontal size={20} />
          </div>
        </div>
        <div className="fb-body">
          <p>{renderTextWithHashtags(postBody || "No content")}</p>
        </div>
        {mediaPreviewUrls.length > 0 && (
          <div
            className="fb-image-placeholder"
            style={{ background: "none", height: "auto", overflow: "hidden" }}
          >
            {renderMediaGrid()}
          </div>
        )}
        <div className="fb-stats">
          <div className="fb-reactions">
            <span className="fb-reaction-icon">
              <ThumbsUp size={14} />
            </span>
            <span className="fb-reaction-icon">
              <Heart size={14} />
            </span>
            <span className="fb-reaction-count">
              {formatNumber(facebookReactionsTotal || metrics.likes)}
            </span>
          </div>
          <div className="fb-shares">
            {formatNumber(metrics.comments)} Comments ·{" "}
            {formatNumber(metrics.shares)} Shares
          </div>
        </div>
        <div className="fb-divider" />
        <div className="fb-actions">
          <div className="fb-action-btn">
            <ThumbsUp size={16} /> Like
          </div>
          <div className="fb-action-btn">
            <MessageCircle size={16} /> Comment
          </div>
          <div className="fb-action-btn">
            <Share2 size={16} /> Share
          </div>
        </div>
      </div>
    );
  };

  const renderLinkedInPreview = () => {
    const liAccount =
      getPreviewAccount("linkedIn") ?? getPreviewAccount("linkedin");
    const liDisplayName = liAccount?.displayName ?? "Nexus AI";

    return (
      <div className="linkedin-preview-container">
        <div className="li-header">
          <Avatar
            src={liAccount?.profilePicture}
            alt={liDisplayName}
            size={48}
            className="li-avatar"
          />
          <div className="li-user-info">
            <div className="li-name">{liDisplayName}</div>
            <div className="li-headline">AI Marketing Assistant</div>
            <div className="li-time">
              {activePlatformData?.posted_at
                ? new Date(activePlatformData.posted_at).toLocaleDateString()
                : "Recently"}{" "}
              •
              <Globe
                size={12}
                style={{ verticalAlign: "middle", opacity: 0.8 }}
              />
            </div>
          </div>
          <div className="li-more">
            <MoreHorizontal size={20} />
          </div>
        </div>
        <div className="li-body">
          <p>{renderTextWithHashtags(postBody || "No content")}</p>
        </div>
        {mediaPreviewUrls.length > 0 && (
          <div
            className="li-image-placeholder"
            style={{ background: "none", height: "auto", overflow: "hidden" }}
          >
            {renderMediaGrid()}
          </div>
        )}
        <div className="li-stats">
          <div className="li-reactions">
            <span className="li-reaction-icon">
              <ThumbsUp size={14} />
            </span>
            <span className="li-reaction-icon">
              <Heart size={14} />
            </span>
            <span className="li-reaction-icon">
              <Lightbulb size={14} />
            </span>
            <span className="li-reaction-count">
              {formatNumber(linkedInLikeCount || metrics.likes)}
            </span>
          </div>
          <div className="li-comments">
            {formatNumber(linkedInCommentCount || metrics.comments)} comments •{" "}
            {formatNumber(linkedInShareCount || metrics.shares)} reposts
          </div>
        </div>
        <div className="li-divider" />
        <div className="li-actions">
          <div className="li-action-btn">
            <ThumbsUp size={16} /> Like
          </div>
          <div className="li-action-btn">
            <MessageCircle size={16} /> Comment
          </div>
          <div className="li-action-btn">
            <Repeat2 size={16} /> Repost
          </div>
          <div className="li-action-btn">
            <Send size={16} /> Send
          </div>
        </div>
      </div>
    );
  };

  const renderRedditPreview = () => {
    const redditAccount = getPreviewAccount("reddit");
    const redditUsername = redditAccount?.username ?? "NexusAI";

    return (
      <div className="reddit-preview-container">
        <div className="rd-header">
          <div className="rd-sub-icon">
            <img
              src={redditIcon}
              alt="Reddit"
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          </div>
          <div className="rd-sub-name">{subreddits?.[0] ?? "r/Subreddit"}</div>
          <div className="rd-meta">• Posted by u/{redditUsername}</div>
        </div>
        <h3 className="rd-title">{postTitle || "No title"}</h3>
        {mediaPreviewUrls.length > 0 && (
          <div className="rd-content">
            <div
              className="rd-image-placeholder"
              style={{ background: "none", height: "auto", overflow: "hidden" }}
            >
              {renderMediaGrid()}
            </div>
          </div>
        )}
        <div className="rd-text-body">
          <p>{renderTextWithHashtags(postBody || "No content")}</p>
        </div>
      </div>
    );
  };

  const renderPreviewContent = () => {
    const norm = activePlatform ? normalizePlatformId(activePlatform) : "";
    switch (norm) {
      case "x":
        return renderTwitterPreview();
      case "threads":
        return renderThreadsPreview();
      case "instagram":
        return renderInstagramPreview();
      case "facebook":
        return renderFacebookPreview();
      case "linkedin":
        return renderLinkedInPreview();
      case "reddit":
        return renderRedditPreview();
      default:
        return renderTwitterPreview();
    }
  };

  /* ─── Loading state ─── */

  if (loading) {
    return (
      <div className="pa-container">
        <div className="pa-header">
          <button className="pa-back-btn" onClick={onBack}>
            <ArrowLeft size={18} /> Back
          </button>
        </div>
        <div className="pa-title-section">
          <h1 className="pa-title">Post Analytics</h1>
          <p className="pa-subtitle">Loading analytics data...</p>
        </div>
        <div className="pa-main">
          <div className="pa-left-col">
            <div className="pa-skeleton pa-skeleton-bar" />
            <div className="pa-skeleton pa-skeleton-card" />
          </div>
          <div className="pa-right-col">
            <div className="pa-skeleton pa-skeleton-card" />
          </div>
        </div>
      </div>
    );
  }

  /* ─── Empty state ─── */

  if (!data || data.platforms.length === 0) {
    return (
      <div className="pa-container">
        <div className="pa-header">
          <button className="pa-back-btn" onClick={onBack}>
            <ArrowLeft size={18} /> Back
          </button>
        </div>
        <div className="pa-no-data">
          <div className="pa-no-data-icon">
            <BarChart3 size={48} />
          </div>
          <h3>No analytics data available</h3>
          <p>
            Analytics data may take a few minutes to appear after publishing.
          </p>
        </div>
      </div>
    );
  }

  /* ─── Main render ─── */

  return (
    <div className="pa-container">
      <div className="pa-header">
        <button className="pa-back-btn" onClick={onBack}>
          <ArrowLeft size={18} /> Back to History
        </button>
      </div>

      <div className="pa-title-section">
        <h1 className="pa-title">Post Analytics</h1>
        <p className="pa-subtitle">
          {item.topic ?? "Published post"} - {item.brand_name}
        </p>
      </div>

      <div className="pa-platform-tabs">
        {data.platforms.map((p) => {
          const meta = getPlatformMeta(p.platform);
          const isActive =
            normalizePlatformId(p.platform) ===
            normalizePlatformId(activePlatform ?? "");
          return (
            <button
              key={`${p.platform}-${meta.name}`}
              className={`pa-platform-tab ${isActive ? "active" : ""}`}
              onClick={() => setActivePlatform(p.platform)}
            >
              {meta.icon ? <img src={meta.icon} alt={meta.name} /> : null}
              {meta.name}
            </button>
          );
        })}
      </div>

      <div className="pa-main">
        <div className="pa-left-col">
          <div className="pa-aggregate-bar">
            <div className="pa-agg-card">
              <div className="pa-agg-icon views">
                <Eye size={18} />
              </div>
              <div className="pa-agg-label">Total Views</div>
              <div className="pa-agg-value">
                {formatNumber(data.aggregate.views)}
              </div>
            </div>
            <div className="pa-agg-card">
              <div className="pa-agg-icon likes">
                <Heart size={18} />
              </div>
              <div className="pa-agg-label">Total Likes</div>
              <div className="pa-agg-value">
                {formatNumber(data.aggregate.likes)}
              </div>
            </div>
            <div className="pa-agg-card">
              <div className="pa-agg-icon comments">
                <MessageCircle size={18} />
              </div>
              <div className="pa-agg-label">Total Comments</div>
              <div className="pa-agg-value">
                {formatNumber(data.aggregate.comments)}
              </div>
            </div>
            <div className="pa-agg-card">
              <div className="pa-agg-icon shares">
                <Share2 size={18} />
              </div>
              <div className="pa-agg-label">Total Shares</div>
              <div className="pa-agg-value">
                {formatNumber(data.aggregate.shares)}
              </div>
            </div>
          </div>

          {activePlatformData && (
            <PlatformAnalyticsCard
              platform={activePlatformData}
              key={activePlatformData.platform}
            />
          )}
        </div>

        <div className="pa-right-col">
          <div className="pa-preview-wrapper">
            <div className="pa-preview-title">Post Preview</div>
            {renderPreviewContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostAnalyticsScreen;
